import { Button, Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useId, useRef } from 'react'
import { TbDownload } from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { fileViewerStore as store } from '../Stores/FileViewerStore'

import styles from './DiskFileViewer.module.css'

/**
 * Документ Office у переглядачі диска — Collabora в режимі перегляду,
 * **усередині** нашого шару: шапка, гортання, «Скачати» й інфопанель лишаються
 * наші, редактор займає всю сцену (на телефоні Collabora сама вмикає
 * мобільний інтерфейс).
 *
 * Відкриття — як у шухляді друку (`OfficeViewer` Ship): прихована форма POST
 * з `access_token` і `access_token_ttl` у `iframe`, тож токен не потрапляє в
 * адресу й історію браузера. Новий файл — новий `key` (токен), тобто новий
 * `iframe`.
 */
export const DiskOfficeView = observer(({ onDownload }: { onDownload: () => void }) => {
    const office = store.office
    const session = office.session
    const frameName = `disk-office-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
    const formRef = useRef<HTMLFormElement | null>(null)
    const frameRef = useRef<HTMLIFrameElement | null>(null)

    useEffect(() => {
        const listener = (event: MessageEvent): void => office.handleMessage(event)

        window.addEventListener('message', listener)

        return () => window.removeEventListener('message', listener)
    }, [office])

    useEffect(() => {
        if (!session) return undefined

        office.attachFrame(frameRef.current)
        formRef.current?.submit()

        return () => office.attachFrame(null)
    }, [session, office])

    if (!session) return null

    return (
        <div className={classNames(styles['office'])} data-testid="disk-viewer-office">
            <form ref={formRef} action={session.actionUrl} method="post" target={frameName} hidden>
                <input type="hidden" name="access_token" value={session.accessToken} readOnly />
                <input type="hidden" name="access_token_ttl" value={String(session.accessTokenTtl)} readOnly />
            </form>
            <iframe
                ref={frameRef}
                name={frameName}
                title={`Перегляд документа: ${session.fileName}`}
                className={classNames(styles['officeFrame'])}
                allow="clipboard-read; clipboard-write; fullscreen"
                data-testid="disk-viewer-office-frame"
            />
            {!office.loaded && (
                <div className={classNames(styles['officeCover'])} data-testid="disk-viewer-office-loading">
                    {office.failed ? (
                        <div className={classNames(styles['fallback'])}>
                            <div className={classNames(styles['fallbackText'])}>
                                Редактор документів не відповідає. Спробуйте відкрити файл пізніше або скачайте його.
                            </div>
                            <Button type="primary" icon={<TbDownload />} onClick={onDownload}>
                                Скачати
                            </Button>
                        </div>
                    ) : (
                        <Spin />
                    )}
                </div>
            )}
        </div>
    )
})
